import type { Room } from '@hollowveil/shared';

/**
 * Hawkins - "Vékeuna l'Incompris"
 *
 * Personnage à aider: Vékeuna (adolescent aux pouvoirs psychiques)
 * Problématique: Harcelé à l'école, s'est réfugié dans l'Upside Down
 *
 * Note: "Vékeuna" est une référence à un enfant qui prononce "Vecna" comme ça
 *
 * Biome: Monde réel = pluie légère, Monde spectral (Upside Down) = orage + éclairs + néons
 *
 * Ce que Côtelette apprend: La patience face à la colère
 *
 * Structure des quêtes:
 * 1. Parler à Dustin et Erica dans le monde réel
 * 2. Collecter les souvenirs de Vékeuna
 * 3. Quête vélo - retrouver les vélos éparpillés
 * 4. La faille s'ouvre vers l'Upside Down
 * 5. Affronter/apaiser Vékeuna dans l'Upside Down
 * 6. Utiliser le walkie-talkie pour communiquer
 */
export const roomHawkinsReveil: Room = {
  id: 'room_hawkins_reveil',
  name: 'Hawkins - Vékeuna l\'Incompris',
  description: 'Une banlieue américaine des années 80. Les habitants parlent d\'une "créature" avec peur...',
  size: [30, 10, 24],
  floorStyle: 'stone',
  biome: 'upside_down',
  initialWorld: 'real',
  hasSpectralLayer: true,
  weather: 'rain',
  weatherIntensity: 0.4,
  ambiance: {
    realWorld: {
      backgroundColor: '#6a7a8a',
      ambientLightColor: '#9ca8b8',
      ambientLightIntensity: 0.6,
      fogColor: '#8a9aaa',
      fogDensity: 0.03,
    },
    spectralWorld: {
      // Upside Down - sombre, orageux, angoissant
      backgroundColor: '#0a0a12',
      ambientLightColor: '#1a3a5c',
      ambientLightIntensity: 0.15,
      fogColor: '#0d1a2d',
      fogDensity: 0.08,
    },
  },
  // Configuration spéciale pour le monde spectral
  spectralWeather: 'storm',
  spectralWeatherIntensity: 0.8,
  objects: [
    // ============================================
    // ROUTE PRINCIPALE (traverse la ville en longueur, nord-sud)
    // ============================================
    {
      id: 'obj_road_main',
      name: 'Route Principale',
      type: 'static',
      modelId: 'road_segment',
      position: [15, 0.01, 12],
      rotation: [0, 0, 0],
      scale: [2, 1, 18],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
    },
    // Route secondaire vers l'école
    {
      id: 'obj_road_school',
      name: 'Route vers l\'École',
      type: 'static',
      modelId: 'road_segment',
      position: [11, 0.01, 18],
      rotation: [0, Math.PI / 2, 0],
      scale: [1.2, 1, 6],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
    },

    // ============================================
    // ÉCOLE DE HAWKINS
    // ============================================
    {
      id: 'obj_hawkins_school',
      name: 'Hawkins Middle School',
      type: 'static',
      modelId: 'hawkins_school',
      position: [8, 0, 20],
      rotation: [0, 0, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { isOccluding: true },
      collider: { type: 'box', size: [6, 4] },
    },

    // Version corrompue de l'école dans l'Upside Down
    {
      id: 'obj_upside_school',
      name: 'Ruines de l\'École',
      type: 'static',
      modelId: 'upside_down_building',
      position: [8, 0, 20],
      rotation: [0, 0, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { isOccluding: true },
      collider: { type: 'box', size: [4, 4] },
    },

    // ============================================
    // MAISONS DU QUARTIER
    // ============================================

    // Maison de Vékeuna (abandonnée)
    {
      id: 'obj_vekeuna_house',
      name: 'Maison Abandonnée',
      type: 'interactive',
      modelId: 'house_abandoned',
      position: [24, 0, 8],
      rotation: [0, -Math.PI / 2, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { isOccluding: true },
      collider: { type: 'box', size: [4, 4] },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'La maison de Vékeuna... Les fenêtres sont condamnées. On sent une présence triste.',
          },
        },
      ],
    },

    // Maison de Dustin - Point de dépôt des vélos
    {
      id: 'obj_dustin_house',
      name: 'Maison de Dustin',
      type: 'interactive',
      modelId: 'suburban_house',
      position: [4, 0, 6],
      rotation: [0, Math.PI / 4, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { variant: 'blue', isOccluding: true },
      collider: { type: 'box', size: [3, 3] },
      onInteract: [
        {
          type: 'showDynamicRiddle',
          params: { riddleType: 'dustin_house' },
        },
      ],
    },

    // Maison voisine
    {
      id: 'obj_neighbor_house',
      name: 'Maison du Voisin',
      type: 'static',
      modelId: 'suburban_house',
      position: [4, 0, 18],
      rotation: [0, Math.PI / 2, 0],
      scale: [1.8, 1.8, 1.8],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { variant: 'brown', isOccluding: true },
      collider: { type: 'box', size: [3, 3] },
    },

    // ============================================
    // NPCs DE QUÊTE - DUSTIN & ERICA
    // ============================================

    // Dustin - près de sa maison
    {
      id: 'obj_dustin',
      name: 'Dustin',
      type: 'interactive',
      modelId: 'npc_dustin',
      position: [7, 0, 8],
      rotation: [0, Math.PI / 3, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        isNPC: true,
        dialogueId: 'dustin_intro',
      },
      onInteract: [
        {
          type: 'showDynamicRiddle',
          params: { riddleType: 'dustin' },
        },
      ],
    },

    // Erica - près de l'école
    {
      id: 'obj_erica',
      name: 'Erica',
      type: 'interactive',
      modelId: 'npc_erica',
      position: [12, 0, 18],
      rotation: [0, -Math.PI / 4, 0],
      scale: [1.1, 1.1, 1.1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        isNPC: true,
        dialogueId: 'erica_intro',
      },
      onInteract: [
        {
          type: 'showDynamicRiddle',
          params: { riddleType: 'erica' },
        },
      ],
    },

    // ============================================
    // FAILLE DIMENSIONNELLE
    // ============================================
    // Faille dans le monde réel - entrée vers l'Upside Down
    {
      id: 'obj_dimensional_rift',
      name: 'Faille Dimensionnelle',
      type: 'interactive',
      modelId: 'dimensional_rift',
      position: [20, 0, 16],
      rotation: [0, 0, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        isOpen: true,
        showAfterRiddleSolved: 'erica_puzzle_done',
      },
      onInteract: [
        {
          type: 'showDynamicRiddle',
          params: { riddleType: 'rift' },
        },
      ],
    },
    // Faille dans l'Upside Down - retour vers le monde réel
    {
      id: 'obj_dimensional_rift_spectral',
      name: 'Faille Dimensionnelle',
      type: 'interactive',
      modelId: 'dimensional_rift',
      position: [20, 0, 16],
      rotation: [0, 0, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        isOpen: true,
      },
      onInteract: [
        {
          type: 'showDynamicRiddle',
          params: { riddleType: 'rift_return' },
        },
      ],
    },

    // ============================================
    // VÉLOS BMX - QUÊTE DE COLLECTE
    // ============================================

    // Vélo de Dustin (déjà trouvé)
    {
      id: 'obj_bike_dustin',
      name: 'Vélo de Dustin',
      type: 'static',
      modelId: 'vintage_bike',
      position: [6, 0, 7],
      rotation: [0, 0.3, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { color: 'red' },
    },

    // Vélo perdu 1 - AU MILIEU POUR DEBUG
    {
      id: 'obj_bike_lost_1',
      name: 'Vélo Bleu de Lucas',
      type: 'collectible',
      modelId: 'vintage_bike',
      position: [15, 0, 15],
      rotation: [0, 0, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        color: 'blue',
        // showAfterRiddleSolved: 'dustin_first_contact', // DEBUG: désactivé
      },
      onInteract: [
        {
          type: 'mountBike',
          params: { color: 'blue' },
        },
        {
          type: 'addToInventory',
          params: {
            itemId: 'bike_blue',
            itemName: 'Vélo Bleu (monté)',
            itemDescription: 'Tu es sur le vélo bleu de Lucas !',
            itemIcon: 'scroll',
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_bike_lost_1' },
        },
      ],
    },

    // Vélo perdu 2 - AU MILIEU POUR DEBUG
    {
      id: 'obj_bike_lost_2',
      name: 'Vélo Jaune de Mike',
      type: 'collectible',
      modelId: 'vintage_bike',
      position: [17, 0, 15],
      rotation: [0, 0.5, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        color: 'yellow',
        // showAfterRiddleSolved: 'dustin_first_contact', // DEBUG: désactivé
      },
      onInteract: [
        {
          type: 'mountBike',
          params: { color: 'yellow' },
        },
        {
          type: 'addToInventory',
          params: {
            itemId: 'bike_yellow',
            itemName: 'Vélo Jaune (monté)',
            itemDescription: 'Tu es sur le vélo jaune de Mike !',
            itemIcon: 'scroll',
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_bike_lost_2' },
        },
      ],
    },

    // Point de rassemblement des vélos - AU MILIEU POUR DEBUG
    {
      id: 'obj_bike_rack',
      name: 'Râtelier à Vélos',
      type: 'interactive',
      modelId: 'bike_rack',
      position: [15, 0, 18],
      rotation: [0, 0, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        // showAfterRiddleSolved: 'dustin_first_contact', // DEBUG: désactivé
      },
      onInteract: [
        {
          type: 'showDynamicRiddle',
          params: { riddleType: 'bike_rack' },
        },
      ],
    },

    // ============================================
    // SOUVENIRS DE VÉKEUNA À COLLECTER
    // ============================================

    // Photo de famille
    {
      id: 'obj_family_photo',
      name: 'Photo de Famille',
      type: 'collectible',
      modelId: 'photo_frame',
      position: [25, 0.5, 9],
      rotation: [0, 0.3, 0],
      scale: [0.8, 0.8, 0.8],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      onInteract: [
        {
          type: 'addToInventory',
          params: {
            itemId: 'vekeuna_memory_1',
            itemName: 'Photo de Famille',
            itemDescription: 'Vékeuna enfant, souriant, avec sa grand-mère.',
            itemIcon: 'scroll',
          },
        },
        {
          type: 'showMessage',
          params: {
            message: 'Une photo de famille... Vékeuna enfant, souriant dans les bras de sa grand-mère.',
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_family_photo' },
        },
      ],
    },

    // Dessin d'ami (près de l'école)
    {
      id: 'obj_friend_drawing',
      name: 'Dessin d\'Ami',
      type: 'collectible',
      modelId: 'scroll',
      position: [6, 0.3, 19],
      rotation: [0, 0.5, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      onInteract: [
        {
          type: 'addToInventory',
          params: {
            itemId: 'vekeuna_memory_2',
            itemName: 'Dessin de Lucas',
            itemDescription: 'Un dessin où deux enfants jouent ensemble. "Vek et Lucas, meilleurs amis"',
            itemIcon: 'scroll',
          },
        },
        {
          type: 'showMessage',
          params: {
            message: '"Vek et Lucas, meilleurs amis pour toujours" - Un dessin d\'enfant touchant.',
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_friend_drawing' },
        },
      ],
    },

    // ============================================
    // WALKIE-TALKIE - QUÊTE DE COMMUNICATION
    // ============================================

    // Walkie-Talkie de Vékeuna (objet clé)
    {
      id: 'obj_walkie_talkie_vekeuna',
      name: 'Walkie-Talkie de Vékeuna',
      type: 'collectible',
      modelId: 'walkie_talkie',
      position: [22, 0.5, 10],
      rotation: [0, 0, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        showAfterRiddleSolved: 'erica_first_contact',
      },
      onInteract: [
        {
          type: 'addToInventory',
          params: {
            itemId: 'walkie_talkie_vekeuna',
            itemName: 'Walkie-Talkie de l\'Amitié',
            itemDescription: 'Le walkie-talkie préféré de Vékeuna. "Sur cette fréquence, on peut parler à n\'importe qui..."',
            itemIcon: 'orb',
          },
        },
        {
          type: 'showMessage',
          params: {
            message: '*KRRR* Le walkie-talkie grésille... "V-Vékeuna ? Tu m\'entends ?" Tu l\'ajoutes à ton inventaire.',
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_walkie_talkie_vekeuna' },
        },
      ],
    },

    // Station Walkie-Talkie (pour communiquer avec Vékeuna)
    {
      id: 'obj_walkie_station',
      name: 'Station de Communication',
      type: 'interactive',
      modelId: 'arcade_cabinet',
      position: [18, 0, 6],
      rotation: [0, Math.PI, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        showAfterRiddleSolved: 'rift_opened',
      },
      onInteract: [
        {
          type: 'showDynamicRiddle',
          params: { riddleType: 'walkie_station' },
        },
      ],
    },

    // ============================================
    // VÉKEUNA (dans l'Upside Down)
    // ============================================
    {
      id: 'obj_vekeuna',
      name: 'Vékeuna',
      type: 'interactive',
      modelId: 'npc_vekeuna',
      position: [15, 0, 16],
      rotation: [0, Math.PI, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        isNPC: true,
        state: 'defensive',
        dialogueId: 'vekeuna_intro',
      },
      onInteract: [
        {
          type: 'showDynamicRiddle',
          params: { riddleType: 'vekeuna' },
        },
      ],
    },

    // ============================================
    // DEMAGORDON (Boss démagogue dans l'Upside Down)
    // ============================================
    {
      id: 'obj_demagordon',
      name: 'Demagordon',
      type: 'interactive',
      modelId: 'npc_demagordon',
      position: [15, 0, 5],
      rotation: [0, 0, 0],
      scale: [2.2, 2.2, 2.2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        isNPC: true,
        isBoss: true,
        dialogueId: 'demagordon_intro',
        hideAfterRiddleSolved: 'demagordon_defeated',
      },
      onInteract: [
        {
          type: 'showDynamicRiddle',
          params: { riddleType: 'demagordon' },
        },
      ],
    },

    // ============================================
    // NÉONS UPSIDE DOWN (spectral)
    // ============================================
    {
      id: 'obj_neon_upside_down',
      name: 'UPSIDE DOWN',
      type: 'static',
      modelId: 'neon_text',
      position: [15, 4, 23],
      rotation: [0, Math.PI, 0],
      scale: [2.5, 0.8, 0.2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        text: 'UPSIDE DOWN',
        glows: true,
        color: '#ff1744',
        blinking: true,
        emissive: true,
        emissiveIntensity: 2,
      },
    },

    // ============================================
    // DÉCORS - MONDE RÉEL
    // ============================================

    // Casier tagué (indice sur le passé de Vékeuna)
    {
      id: 'obj_locker_tagged',
      name: 'Casier Tagué',
      type: 'interactive',
      modelId: 'locker',
      position: [11, 0, 21],
      rotation: [0, Math.PI, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Un vieux casier d\'école... tagué d\'insultes. "MONSTRE", "FREAK"... Pauvre Vékeuna.',
          },
        },
      ],
    },

    // Pylône électrique
    {
      id: 'obj_pylon_1',
      name: 'Pylône Électrique',
      type: 'static',
      modelId: 'electric_pylon',
      position: [28, 0, 2],
      rotation: [0, 0, 0],
      scale: [1.5, 2, 1.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { isOccluding: true },
    },

    // Guirlandes de Noël (ambiance Stranger Things)
    {
      id: 'obj_christmas_lights_1',
      name: 'Guirlandes',
      type: 'static',
      modelId: 'christmas_lights',
      position: [24, 2.5, 9],
      rotation: [0, 0, 0],
      scale: [4, 1, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { blinking: true, color: '#ff6b6b' },
    },

    // Bus scolaire
    {
      id: 'obj_school_bus',
      name: 'Bus Scolaire',
      type: 'static',
      modelId: 'school_bus',
      position: [14, 0, 22],
      rotation: [0, -Math.PI / 2, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { isOccluding: true },
      collider: { type: 'box', size: [5, 2] },
    },

    // Lampadaires
    {
      id: 'obj_streetlamp_1',
      name: 'Lampadaire',
      type: 'static',
      modelId: 'streetlamp',
      position: [2, 0, 12],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_streetlamp_2',
      name: 'Lampadaire',
      type: 'static',
      modelId: 'streetlamp',
      position: [28, 0, 12],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_streetlamp_3',
      name: 'Lampadaire',
      type: 'static',
      modelId: 'streetlamp',
      position: [15, 0, 4],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Boîtes aux lettres
    {
      id: 'obj_mailbox_1',
      name: 'Boîte aux Lettres',
      type: 'static',
      modelId: 'mailbox',
      position: [6, 0, 5],
      rotation: [0, Math.PI / 4, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
    },
    {
      id: 'obj_mailbox_2',
      name: 'Boîte aux Lettres',
      type: 'static',
      modelId: 'mailbox',
      position: [22, 0, 7],
      rotation: [0, -Math.PI / 2, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
    },

    // Voiture années 80
    {
      id: 'obj_car_80s',
      name: 'Ford Pinto',
      type: 'static',
      modelId: 'car_80s',
      position: [3, 0, 14],
      rotation: [0, Math.PI / 2, 0],
      scale: [1.3, 1.3, 1.3],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { color: 'brown', isOccluding: true },
      collider: { type: 'box', size: [4, 2] },
    },

    // Version cassée dans l'Upside Down
    {
      id: 'obj_car_wrecked',
      name: 'Épave de Voiture',
      type: 'static',
      modelId: 'car_wrecked',
      position: [3, 0, 14],
      rotation: [0, Math.PI / 2 + 0.2, 0],
      scale: [1.3, 1.3, 1.3],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { isOccluding: true },
      collider: { type: 'box', size: [4, 2] },
    },

    // Arbres (réel)
    {
      id: 'obj_tree_1',
      name: 'Arbre',
      type: 'static',
      modelId: 'oak_tree',
      position: [2, 0, 3],
      rotation: [0, 0.3, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { isOccluding: true },
    },
    {
      id: 'obj_tree_2',
      name: 'Arbre',
      type: 'static',
      modelId: 'oak_tree',
      position: [26, 0, 20],
      rotation: [0, -0.4, 0],
      scale: [1.8, 1.8, 1.8],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { isOccluding: true },
    },
    {
      id: 'obj_tree_3',
      name: 'Arbre',
      type: 'static',
      modelId: 'oak_tree',
      position: [28, 0, 8],
      rotation: [0, 0.8, 0],
      scale: [1.4, 1.4, 1.4],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { isOccluding: true },
    },

    // Arbres morts (Upside Down)
    {
      id: 'obj_dead_tree_1',
      name: 'Arbre Mort',
      type: 'static',
      modelId: 'dead_tree',
      position: [2, 0, 3],
      rotation: [0, 0.3, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { isOccluding: true },
    },
    {
      id: 'obj_dead_tree_2',
      name: 'Arbre Mort',
      type: 'static',
      modelId: 'dead_tree',
      position: [26, 0, 20],
      rotation: [0, -0.5, 0],
      scale: [1.8, 1.8, 1.8],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { isOccluding: true },
    },
    {
      id: 'obj_dead_tree_3',
      name: 'Arbre Mort',
      type: 'static',
      modelId: 'dead_tree',
      position: [6, 0, 22],
      rotation: [0, 0.1, 0],
      scale: [1.3, 1.3, 1.3],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { isOccluding: true },
    },
    {
      id: 'obj_dead_tree_4',
      name: 'Arbre Mort',
      type: 'static',
      modelId: 'dead_tree',
      position: [28, 0, 8],
      rotation: [0, 0.8, 0],
      scale: [1.4, 1.4, 1.4],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { isOccluding: true },
    },

    // Lianes/Vignes de l'Upside Down
    {
      id: 'obj_vines_1',
      name: 'Lianes',
      type: 'static',
      modelId: 'vines',
      position: [2, 0, 14],
      rotation: [0, 0, 0],
      scale: [2, 3, 2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
    },
    {
      id: 'obj_vines_2',
      name: 'Lianes',
      type: 'static',
      modelId: 'vines',
      position: [28, 0, 18],
      rotation: [0, 0.5, 0],
      scale: [2, 3, 2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
    },

    // Bâtiment Upside Down additionnel
    {
      id: 'obj_upside_building_1',
      name: 'Ruine',
      type: 'static',
      modelId: 'upside_down_building',
      position: [4, 0, 6],
      rotation: [0, 0.2, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { isOccluding: true },
      collider: { type: 'box', size: [3, 3] },
    },

    // ============================================
    // PORTAILS
    // ============================================

    // Portail retour vers le Sanctuaire
    {
      id: 'obj_portal_back',
      name: 'Portail vers le Sanctuaire',
      type: 'interactive',
      modelId: 'portal_sanctuary',
      position: [15, 0, 1],
      rotation: [0, 0, 0],
      scale: [2, 3, 0.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        portalTo: 'room_sanctuary',
        portalColor: '#9d8cff',
      },
      onInteract: [
        {
          type: 'loadRoom',
          params: { roomId: 'room_sanctuary' },
        },
      ],
    },

    // ============================================
    // ORACLE (apparaît pour donner des conseils)
    // ============================================
    {
      id: 'obj_oracle_appearance',
      name: 'L\'Oracle Chevelu',
      type: 'interactive',
      modelId: 'npc_oracle_chevelu',
      position: [10, 0, 4],
      rotation: [0, Math.PI / 3, 0],
      scale: [0.9, 0.9, 0.9],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        isNPC: true,
        dialogueId: 'oracle_hawkins_hint',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Oracle: *perruque qui grésille* "Ah, Hawkins ! Ici, la colère d\'un enfant a déchiré la réalité. Pour l\'aider, il faut d\'abord comprendre sa douleur. Parle à ses anciens amis..."',
          },
        },
      ],
    },
  ],
  puzzles: [
    // Collecter les souvenirs positifs
    {
      id: 'puzzle_vekeuna_memories',
      name: 'Les Souvenirs de Vékeuna',
      type: 'collect',
      state: 'active',
      description: 'Trouve les souvenirs positifs de Vékeuna pour lui montrer qu\'il n\'est pas un monstre.',
      hint: 'Cherche dans sa maison et chez les voisins.',
      linkedObjectIds: ['obj_family_photo', 'obj_friend_drawing', 'obj_walkie_talkie_vekeuna'],
      solution: {
        type: 'items',
        expected: ['vekeuna_memory_1', 'vekeuna_memory_2', 'walkie_talkie_vekeuna'],
      },
      onSolve: [
        {
          type: 'showMessage',
          params: {
            message: 'Tu as rassemblé tous les souvenirs de Vékeuna. Il est temps de lui parler.',
          },
        },
      ],
    },
    // Quête des vélos
    {
      id: 'puzzle_bike_quest',
      name: 'Les Vélos Perdus',
      type: 'collect',
      state: 'locked',
      description: 'Retrouve les vélos abandonnés par les amis de Vékeuna quand ils ont fui.',
      linkedObjectIds: ['obj_bike_lost_1', 'obj_bike_lost_2'],
      solution: {
        type: 'items',
        expected: ['bike_blue', 'bike_yellow'],
      },
      onSolve: [
        {
          type: 'showMessage',
          params: {
            message: 'Tu as récupéré tous les vélos ! Ramène-les au râtelier.',
          },
        },
      ],
    },
  ],
  connections: [
    {
      targetRoomId: 'room_sanctuary',
      position: [15, 0, 1],
      direction: 'north',
      locked: false,
    },
  ],
  npcs: [
    // Chat de quartier
    {
      id: 'npc_neighborhood_cat',
      type: 'fox',
      name: 'Chat de Quartier',
      position: [12, 0, 8],
      scale: 0.9,
      idleAnimation: 'sit',
      petAnimation: 'purr',
      petRadius: 2,
      petCooldown: 3000,
      isPettable: true,
      visibleInWorld: 'real',
    },
    // Chien du voisin
    {
      id: 'npc_neighbor_dog',
      type: 'wolf',
      name: 'Chien du Voisin',
      position: [6, 0, 16],
      scale: 1.1,
      idleAnimation: 'stand',
      petAnimation: 'happy',
      petRadius: 2.5,
      petCooldown: 3000,
      isPettable: true,
      visibleInWorld: 'real',
    },
    // Démochien apprivoisé (dans l'Upside Down)
    {
      id: 'npc_demodog',
      type: 'wolf',
      name: 'Petit Démo-chien',
      position: [8, 0, 8],
      scale: 0.8,
      idleAnimation: 'sit',
      petAnimation: 'nuzzle',
      petRadius: 2,
      petCooldown: 3000,
      isPettable: true,
      visibleInWorld: 'spectral',
    },
  ],
};
